import axios, { AxiosError, AxiosRequestConfig } from "axios";

/**
 * Единый переключатель dev/prod в WebApp — берём URL API в зависимости
 * от `REACT_APP_PROD_MODE`.
 *   • DEV  → REACT_APP_API_BASE_URL_DEV (обычно пусто = относительный
 *            /api/v1, CRA-proxy переадресует на localhost:5555).
 *   • PROD → REACT_APP_API_BASE_URL_PROD (напр. https://api.zero-wallet.net).
 *
 * Fallback: если новые переменные не заданы — используем старую
 * `REACT_APP_API_BASE_URL` (обратная совместимость).
 */
const getBaseURL = () => {
  const isProd =
    (process.env.REACT_APP_PROD_MODE ?? "").toLowerCase() === "true";
  const envURL =
    (isProd
      ? process.env.REACT_APP_API_BASE_URL_PROD
      : process.env.REACT_APP_API_BASE_URL_DEV) ??
    process.env.REACT_APP_API_BASE_URL;

  // Env не задан → относительный путь /api/v1 (для CRA-proxy или прод-reverse-proxy).
  if (!envURL) return "/api/v1";
  // Env задан с /api/v1 в конце — используем как есть.
  if (envURL.endsWith("/api/v1")) return envURL;
  // Иначе — дописываем /api/v1.
  const cleanURL = envURL.replace(/\/$/, "");
  return `${cleanURL}/api/v1`;
};

export const api = axios.create({
  baseURL: getBaseURL(),
  // withCredentials: true нужен для веб-версии — httpOnly refresh-cookie
  // на CROSS-origin (app.zero-wallet.net → api.zero-wallet.net) ходит
  // только когда есть этот флаг + бэкенд ставит `Access-Control-Allow-
  // Credentials: true` и origin явно указан (не "*"). В Telegram Mini App
  // мы вообще не используем cookie, но флаг ничему не мешает.
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers["access-token"] = token;
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    // Обходит interstitial-страницу ngrok free ("You are about to visit…").
    // На проде без ngrok header просто игнорируется.
    config.headers["ngrok-skip-browser-warning"] = "true";
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Один раз попытаемся перелогиниться при 401, чтобы не показывать пустое состояние
 * после протухшего токена (JWT TTL). Промис шарим, чтобы параллельные запросы
 * не начали 10 логинов одновременно.
 */
let refreshPromise: Promise<string | null> | null = null;

async function attemptRefresh(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const { getAccessToken } = await import("../login");
      const token = await getAccessToken();
      return token;
    } catch {
      return null;
    } finally {
      // Пусть в течение 1 секунды новый запрос не поднимает refresh снова.
      setTimeout(() => (refreshPromise = null), 1000);
    }
  })();
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const config = error.config as AxiosRequestConfig & { _retried?: boolean };

    // Редиректим на /maintenance только если бэкенд явно этого попросил —
    // например, при глобально включённом режиме техработ (специальный
    // заголовок X-Maintenance-Mode). Раньше на maintenance уводил
    // любой 503, что было ошибкой: 503 от отдельного модуля (напр.
    // недонастроенный DV.net) не должен ронять весь кабинет.
    const maintenanceHeader = (
      error.response?.headers as Record<string, string> | undefined
    )?.["x-maintenance-mode"];
    if (status === 503 && maintenanceHeader === "true") {
      if (typeof window !== "undefined" && window.location.pathname !== "/maintenance") {
        window.location.href = "/maintenance";
      }
      return Promise.reject(error);
    }

    // Auth-эндпоинты сами являются шагами логина, ретрать их нельзя.
    // Иначе на веб-версии при первом заходе (когда refresh-cookie ещё
    // нет) `/auth/web/refresh` → 401 → interceptor вызывает refresh →
    // снова `/auth/web/refresh` → 401 → снова refresh… deadlock,
    // splash-скрин висит навсегда, юзер в форму логина не попадает.
    const url = String(config?.url ?? "");
    const isAuthEndpoint =
      url.includes("/auth/telegram") ||
      url.includes("/auth/web/refresh") ||
      url.includes("/auth/web/send-code") ||
      url.includes("/auth/web/verify-code") ||
      url.includes("/auth/web/pin-login") ||
      url.includes("/auth/web/register") ||
      url.includes("/auth/web/logout");

    if (status === 401 && config && !config._retried && !isAuthEndpoint) {
      config._retried = true;
      const newToken = await attemptRefresh();
      if (newToken) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>)["access-token"] = newToken;
        (config.headers as Record<string, string>)["Authorization"] = `Bearer ${newToken}`;
        return api.request(config);
      }
    }

    return Promise.reject(error);
  },
);
